import {
  checkoutAmount,
  DEFAULT_TIERED_MIN,
  exclusivePromoCodes,
  getTieredPricing,
  resolveCheckoutPrice,
  StripePriceInfo,
  stripePriceMapping,
  stripePricingIds,
} from "@Containers/web-portal/utils/stripe";
import { useDialogManager } from "@Context/common/general/dialog-manager-context";
import storage from "@Context/storage";
import { defaultState, PaymentContextType } from "@Context/web-portal/payment/payment-context.utils";
import { calculateFinalPrice, getExclusivePromoCode, isCouponValidForPrice } from "@Context/web-portal/payment/stripe-utils";
import { Api } from "@Shared/schemas";
import { CouponResponse, StripeSubscriptionDetails, StripeSubscriptionPrice } from "@Shared/schemas/stripe/stripeSchemas";
import { useApi, useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import BillingCountries from "@shared/enums/BillingCountries";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";

export const PaymentContext = createContext<PaymentContextType>({
  ...defaultState,
});

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

interface PaymentProviderProps {
  children: ReactNode;
}

const PaymentProvider = ({ children }: PaymentProviderProps) => {
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const userId = globalState.user?.id;
  const { openDialog, isDialogRegistered, dialogRegistry } = useDialogManager();

  const { call: getProducts } = useApi<typeof Api.Stripe.GetProducts>({
    api: Api.Stripe.GetProducts,
    request: {} as any,
  });

  const {
    call: getCoupon,
    loading: promoCodeLoading,
  } = useApi<typeof Api.Stripe.GetCoupon>({
    api: Api.Stripe.GetCoupon,
    request: {} as any,
  });

  const { call: getSubscriptionDetails } = useApi<typeof Api.Stripe.GetSubscriptionDetails>({
    api: Api.Stripe.GetSubscriptionDetails,
    request: {} as any,
  });

  /*---------------------------------
   * State Management
   *---------------------------------*/
  // UI state
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Price and subscription state
  const [selectedPriceId, setSelectedPriceId] = useState<string>();
  const [selectedPriceInfo, setSelectedPriceInfo] = useState<StripePriceInfo>();
  const [catalogPrices, setPrices] = useState<StripeSubscriptionPrice[]>([]);
  const [billingCountry, setBillingCountry] = useState("");
  const [isBillingComplete, setIsBillingComplete] = useState(false);
  const basePrices = catalogPrices.filter(price =>
    ([stripePricingIds.MULTI_SEAT_MONTHLY, stripePricingIds.MULTI_SEAT_ANNUALLY] as string[]).includes(price.id)
  );
  const resolvedPrices = basePrices.map(price => ({
    base: price,
    resolved: resolveCheckoutPrice(
      price,
      billingCountry,
      catalogPrices,
      price.id === stripePricingIds.MULTI_SEAT_MONTHLY ? stripePricingIds.MULTI_SEAT_MONTHLY_NOK : stripePricingIds.MULTI_SEAT_ANNUALLY_NOK
    ),
  }));
  const selectedResolvedPrice = resolvedPrices.find(price => price.base.id === selectedPriceId)?.resolved;
  const isCurrencyUnavailable = billingCountry === BillingCountries.NORWAY && !selectedResolvedPrice;
  // Keep plan IDs stable for existing plan labels, selection and promo restrictions.
  const prices = resolvedPrices.map(({ base, resolved }) => ({ ...(resolved || base), id: base.id }));
  const purchasePriceId = selectedResolvedPrice?.id;
  const displayPrice = selectedResolvedPrice || basePrices.find(price => price.id === selectedPriceId);
  const priceAmount = { amount: displayPrice?.unit_amount || 0, currency: displayPrice?.currency || "" };
  const tieredPricing = displayPrice ? getTieredPricing(displayPrice) || undefined : undefined;
  const [quantity, setQuantity] = useState(DEFAULT_TIERED_MIN);

  // Promo code state
  const [promoCode, setPromoCode] = useState<string>("");
  const [promoCodeError, setPromoCodeError] = useState("");
  const [coupon, setCoupon] = useState<CouponResponse>();

  // Incomplete subscription state
  const [incompleteSubscription, setIncompleteSubscription] = useState<StripeSubscriptionDetails | null>(null);

  /*---------------------------------
   * Price and Product Selection
   *---------------------------------*/

  // Calculate base price using selected price and quantity
  const basePrice = displayPrice ? checkoutAmount(displayPrice, quantity) : 0;

  // Calculate final price after discount
  const isCouponCurrencyValid =
    !coupon ||
    ((!coupon.amountOff || coupon.currency === priceAmount.currency) &&
      (!coupon.restrictions.minimum_amount_currency || coupon.restrictions.minimum_amount_currency === priceAmount.currency));
  const finalPrice = calculateFinalPrice(basePrice, isCouponCurrencyValid ? coupon : undefined);

  // Payment details for Stripe
  const paymentDetails = {
    amount: Math.round(finalPrice),
    currency: priceAmount.currency,
  };

  const setSelectedPriceState = (priceId: string) => {
    setSelectedPriceId(priceId);
    setSelectedPriceInfo(stripePriceMapping[priceId]);
    setPromoCodeError("");
  };

  /*---------------------------------
   * Promo Code Management
   *---------------------------------*/

  const handlePromoCodeError = (message = i18n("checkout.error.promo.code.invalid.generic")) => {
    setPromoCodeError(message);
    setCoupon(undefined);
    setPromoCode("");
  };

  const checkPromoCode = (coupon: CouponResponse, promoCode: string, basePrice: number, selectedPriceInfo?: StripePriceInfo) => {
    const isPriceValid =
      isCouponValidForPrice(coupon, basePrice) &&
      (!coupon.amountOff || coupon.currency === priceAmount.currency) &&
      (!coupon.restrictions.minimum_amount_currency || coupon.restrictions.minimum_amount_currency === priceAmount.currency);
    const exclusivePromoCode = getExclusivePromoCode(promoCode, selectedPriceInfo?.pricingId || "", exclusivePromoCodes);
    const isValid = isPriceValid && !exclusivePromoCode;

    return { isValid, exclusivePromoCode, isPriceValid };
  };

  const applyPromoCode = (param: { promoCode?: string; couponId?: string }) => {
    if (!param.promoCode && !param.couponId) return;

    setPromoCodeError("");

    const { promoCode, couponId } = param;
    const request = promoCode ? { promoCode } : { couponId };

    getCoupon(request)
      .then(res => {
        const { isValid, exclusivePromoCode } = checkPromoCode(res.coupon, promoCode || "", basePrice, selectedPriceInfo);
        if (isValid) {
          setCoupon(res.coupon);
          if (promoCode) setPromoCode(promoCode);
        } else {
          handlePromoCodeError(exclusivePromoCode?.errorMessage || i18n("checkout.error.promo.code.invalid.subscription", { promoCode }));
        }
      })
      .catch(err => {
        handlePromoCodeError(err.message || undefined);
      });
  };

  useEffect(() => {
    if (!coupon) {
      return;
    }

    const { isValid, exclusivePromoCode } = checkPromoCode(coupon, promoCode, basePrice, selectedPriceInfo);
    if (isValid) {
      setPromoCodeError("");
    } else {
      handlePromoCodeError(exclusivePromoCode?.errorMessage || i18n("checkout.error.promo.code.invalid.subscription", { promoCode: promoCode }));
    }
  }, [coupon, promoCode, basePrice, selectedPriceInfo, priceAmount.currency]);

  useEffect(() => {
    // Check for existing incomplete subscription with outstanding invoice
    if (userId && isDialogRegistered("outstanding-invoice")) {
      getSubscriptionDetails({ user_id: userId, include_incomplete: true })
        .then(res => {
          if (res.data && res.data.length > 0) {
            // Find the most recent incomplete subscription that hasn't expired (within 23 hours)
            const now = Math.floor(Date.now() / 1000);
            const incompleteExpireThreshold = 23 * 60 * 60; // 23 hours in seconds

            const validIncomplete = res.data.find((sub: StripeSubscriptionDetails) => {
              if (sub.status !== "incomplete") return false;
              const timeSinceCreation = now - sub.created;
              return timeSinceCreation < incompleteExpireThreshold;
            });

            if (validIncomplete) {
              setIncompleteSubscription(validIncomplete);

              console.log("validIncomplete", validIncomplete);
              // If there's an outstanding invoice with a payment URL, show the dialog
              if (
                validIncomplete.latest_invoice &&
                typeof validIncomplete.latest_invoice === "object" &&
                "hosted_invoice_url" in validIncomplete.latest_invoice &&
                validIncomplete.latest_invoice.hosted_invoice_url
              ) {
                openDialog("outstanding-invoice", {
                  invoiceUrl: validIncomplete.latest_invoice.hosted_invoice_url,
                  amountDue: validIncomplete.latest_invoice.amount_due,
                  currency: validIncomplete.latest_invoice.currency,
                  variant: "checkout",
                });
              }
            }
          }
        })
        .catch(() => {
          // Ignore errors - user may not have a Stripe customer yet
        });
    }
  }, [userId, dialogRegistry, isDialogRegistered]);

  useEffect(() => {
    const subscriptionInterval = storage.get({ key: "subscription_interval" });
    const initialPriceId = subscriptionInterval === "month" ? stripePricingIds.MULTI_SEAT_MONTHLY : stripePricingIds.MULTI_SEAT_ANNUALLY;
    setSelectedPriceState(initialPriceId);

    getProducts().then(res => {
      setPrices(res.data);
    });

    // If the user is a rewardful user, apply the rewardful coupon
    if (window.Rewardful && window.Rewardful.coupon) {
      window.rewardful("ready", function () {
        if (window.Rewardful?.coupon) {
          applyPromoCode({ couponId: window.Rewardful.coupon.id });
        }
      });
    }

    return () => {
      storage.remove({ key: "subscription_interval" });
    };
  }, []);

  useEffect(() => {
    if (prices.length > 0 && selectedPriceId) {
      setIsLoading(false);
    }
  }, [catalogPrices, selectedPriceId, billingCountry]);

  const openOutstandingInvoiceDialog = () => {
    if (
      incompleteSubscription?.latest_invoice &&
      typeof incompleteSubscription.latest_invoice === "object" &&
      "hosted_invoice_url" in incompleteSubscription.latest_invoice &&
      incompleteSubscription.latest_invoice.hosted_invoice_url
    ) {
      openDialog("outstanding-invoice", {
        invoiceUrl: incompleteSubscription.latest_invoice.hosted_invoice_url,
        amountDue: incompleteSubscription.latest_invoice.amount_due,
        currency: incompleteSubscription.latest_invoice.currency,
        variant: "checkout",
      });
    }
  };

  const value: PaymentContextType = {
    selectedPriceId,
    purchasePriceId,
    billingCountry,
    billingComplete: isBillingComplete,
    currencyUnavailable: isCurrencyUnavailable,
    setBillingCountry,
    setBillingComplete: setIsBillingComplete,
    selectedPriceInfo,
    tieredPricing,
    prices,
    finalPrice,
    basePrice,
    paymentDetails,
    coupon,
    promoCode,
    promoCodeError,
    promoCodeLoading,
    isLoading,
    productSelectionDialogOpen,
    incompleteSubscription,
    quantity,

    applyPromoCode,
    setSelectedPriceState,
    setProductSelectionDialogOpen,
    setQuantity,
    openOutstandingInvoiceDialog,
  };

  return <PaymentContext.Provider value={value as PaymentContextType}>{children}</PaymentContext.Provider>;
};

export default PaymentProvider;
